import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import AutoComplete from '../../../components/AutoComplete';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Region as IRegion } from '../../../store/region/api/interface';
import { useFormik } from 'formik';
import {
  createRegion,
  getRegion,
  patchRegion,
} from '../../../store/region/api';
import { setStatus } from '../../../store/region/regionSlice';
import { useEffect, useState } from 'react';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';
import {
  setStatus as selectRegionSetStatus,
  reset as resetSelectRegionReset,
} from '../../../store/selectRegion/selectRegionSlice';
import regionSchema from '../../../validation/regionSchema';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import {
  findSelectCountry,
  searchSelectCountry,
} from '../../../store/selectCountry/api';
import {
  setSkip as setSkipCountry,
  reset as resetCountry,
} from '../../../store/selectCountry/selectCountrySlice';

const RegionForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IRegion; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.region);
  const [response, setResponse] = useState<any>();

  const {
    handleSubmit,
    values,
    setValues,
    handleChange,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      code: '',
      countryId: '',
      // ccy: '',
      decimalPlaces: '',
      deliveryFee: '',
      vatRate: '',
      printingCharges: '',
      // deliveryDayGap: '1',
      // deliveryUpto: '',
      isVatOnus: true,
      // isMessageCardEnabled: true,
      // isGiftCodeEnabled: true,
      // isEarlyAccess: true,
      // isWalletEnabled: true,
      // isAddonEnabled: true,
      // isCakeColorPickerEnabled: true,
      // isWriteColorPickerEnabled: true,
      // isToppingsOndemandLabelFastDelivery: true,
      activeState: true,
    },
    validationSchema: regionSchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createRegion(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.country;
          await dispatch(patchRegion(reqBody)).unwrap();
        }
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectRegionReset());
        dispatch(selectRegionSetStatus({ status: 'idle' }));
        navigation('/region');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        setValues(state);
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getRegion({ id: decodeParam })
          ).unwrap();
          setValues(record);
          setResponse(record);
        } catch (error: any) {
          navigation('/region');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, setValues, state]);

  // Country Data
  const {
    data: countryData,
    isLoading: isCountryLoading,
    skip: countrySkip,
    hasMore: countryHasMore,
    status: countryStatus,
  } = useAppSelector((state) => state.selectCountry);

  useEffect(() => {
    if (countryStatus === 'idle') {
      dispatch(
        findSelectCountry({
          limit: 10,
          skip: countrySkip,
        })
      );
    }
  }, [countrySkip, countryStatus]);

  const onScrollFetchCountry = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      countryHasMore
    ) {
      dispatch(setSkipCountry({ skip: countrySkip + 10 }));
      dispatch(
        findSelectCountry({
          limit: 10,
          skip: countrySkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchCountry = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetCountry());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipCountry({ skip: 0 }));
    dispatch(searchSelectCountry(params));
  };

  const uniqueCountryData = uniqBy(
    [...(response?.country ? [response?.country] : []), ...countryData],
    'id'
  );

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <Card sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: {
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  },
                }}
              >
                <TextField
                  fullWidth
                  label={'Label English'}
                  name='labelEn'
                  id='labelEn'
                  onChange={handleChange}
                  value={values.labelEn}
                  error={Boolean(errors.labelEn && touched.labelEn)}
                  helperText={
                    Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                  }
                />
                <TextField
                  fullWidth
                  label={'Label Arabic'}
                  name='labelAr'
                  id='labelAr'
                  onChange={handleChange}
                  value={values.labelAr}
                  error={Boolean(errors.labelAr && touched.labelAr)}
                  helperText={
                    Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                  }
                />
                <TextField
                  fullWidth
                  label={'Code'}
                  name='code'
                  id='code'
                  onChange={handleChange}
                  value={values.code}
                  error={Boolean(errors.code && touched.code)}
                  helperText={
                    Boolean(errors.code && touched.code) && errors.code
                  }
                />
                <AutoComplete
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('countryId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('countryId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(touched.countryId && errors.countryId)}
                  helperText={
                    Boolean(touched.countryId && errors.countryId)
                      ? errors.countryId
                      : ''
                  }
                  fetchFunction={onScrollFetchCountry}
                  label={'Select Country'}
                  loading={isCountryLoading}
                  options={uniqueCountryData}
                  searchFunction={searchCountry}
                  value={
                    uniqueCountryData?.filter(
                      (country) => country.id === values.countryId
                    )?.[0] || null
                  }
                />
                {/* <TextField
                  fullWidth
                  label={'CCY'}
                  name='ccy'
                  id='ccy'
                  onChange={handleChange}
                  value={values.ccy}
                  error={Boolean(errors.ccy && touched.ccy)}
                  helperText={Boolean(errors.ccy && touched.ccy) && errors.ccy}
                /> */}

                <TextField
                  fullWidth
                  label={'Decimal Places'}
                  name='decimalPlaces'
                  id='decimalPlaces'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.decimalPlaces}
                  error={Boolean(errors.decimalPlaces && touched.decimalPlaces)}
                  helperText={
                    Boolean(errors.decimalPlaces && touched.decimalPlaces) &&
                    errors?.decimalPlaces
                  }
                />

                <TextField
                  fullWidth
                  label={'Delivery Fee'}
                  name='deliveryFee'
                  id='deliveryFee'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.deliveryFee}
                  error={Boolean(errors.deliveryFee && touched.deliveryFee)}
                  helperText={
                    Boolean(errors.deliveryFee && touched.deliveryFee) &&
                    errors?.deliveryFee
                  }
                />

                {/* <TextField
                  fullWidth
                  label={'Delivery Gap'}
                  name='deliveryDayGap'
                  id='deliveryDayGap'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.deliveryDayGap}
                  error={Boolean(
                    errors.deliveryDayGap && touched.deliveryDayGap
                  )}
                  helperText={
                    Boolean(errors.deliveryDayGap && touched.deliveryDayGap) &&
                    errors?.deliveryDayGap
                  }
                /> */}

                <TextField
                  fullWidth
                  label={'VAT Rate'}
                  name='vatRate'
                  id='vatRate'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.vatRate}
                  error={Boolean(errors.vatRate && touched.vatRate)}
                  helperText={
                    Boolean(errors.vatRate && touched.vatRate) &&
                    errors?.vatRate
                  }
                />
                <TextField
                  fullWidth
                  label={'Printing Charges'}
                  name='printingCharges'
                  id='printingCharges'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.printingCharges}
                  error={Boolean(
                    errors.printingCharges && touched.printingCharges
                  )}
                  helperText={
                    Boolean(
                      errors.printingCharges && touched.printingCharges
                    ) && errors?.printingCharges
                  }
                />
                {/* <TextField
                  fullWidth
                  label={'Delivery Upto'}
                  name='deliveryUpto'
                  id='deliveryUpto'
                  onChange={handleChange}
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                  }}
                  value={values.deliveryUpto}
                  error={Boolean(errors.deliveryUpto && touched.deliveryUpto)}
                  helperText={
                    Boolean(errors.deliveryUpto && touched.deliveryUpto) &&
                    errors?.deliveryUpto
                  }
                /> */}
              </Box>

              <Grid
                container
                spacing={2}
                py={2}
              >
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isMessageCardEnabled'
                        onChange={(e) =>
                          setFieldValue(
                            'isMessageCardEnabled',
                            e.target.checked
                          )
                        }
                        checked={values.isMessageCardEnabled}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Message Card Enabled'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isGiftCodeEnabled'
                        onChange={(e) =>
                          setFieldValue('isGiftCodeEnabled', e.target.checked)
                        }
                        checked={values.isGiftCodeEnabled}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Gift Card Enabled'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isEarlyAccess'
                        onChange={(e) =>
                          setFieldValue('isEarlyAccess', e.target.checked)
                        }
                        checked={values.isEarlyAccess}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Early Access'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isWalletEnabled'
                        onChange={(e) =>
                          setFieldValue('isWalletEnabled', e.target.checked)
                        }
                        checked={values.isWalletEnabled}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Wallet Enabled'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isAddonEnabled'
                        onChange={(e) =>
                          setFieldValue('isAddonEnabled', e.target.checked)
                        }
                        checked={values.isAddonEnabled}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Addon Enabled'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isCakeColorPickerEnabled'
                        onChange={(e) =>
                          setFieldValue(
                            'isCakeColorPickerEnabled',
                            e.target.checked
                          )
                        }
                        checked={values.isCakeColorPickerEnabled}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Cake Colour Picker Enabled'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isWriteColorPickerEnabled'
                        onChange={(e) =>
                          setFieldValue(
                            'isWriteColorPickerEnabled',
                            e.target.checked
                          )
                        }
                        checked={values.isWriteColorPickerEnabled}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Write Colour Picker Enabled'}
                  />
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isToppingsOndemandLabelFastDelivery'
                        onChange={(e) =>
                          setFieldValue(
                            'isToppingsOndemandLabelFastDelivery',
                            e.target.checked
                          )
                        }
                        checked={values.isToppingsOndemandLabelFastDelivery}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Fast Delivery'}
                  />
                </Grid> */}
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='isVatOnus'
                        onChange={(e) =>
                          setFieldValue('isVatOnus', e.target.checked)
                        }
                        checked={values.isVatOnus}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'VAT on us'}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='activeState'
                        onChange={(e) =>
                          setFieldValue('activeState', e.target.checked)
                        }
                        checked={values.activeState}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                      />
                    }
                    label={'Active Status'}
                  />
                </Grid>
              </Grid>

              <Stack
                alignItems='center'
                sx={{ mt: 3 }}
                direction='row'
                justifyContent='flex-end'
                gap={1}
              >
                <Button
                  variant='outlined'
                  onClick={() => navigation('/region')}
                >
                  {'Cancel'}
                </Button>

                <LoadingButton
                  type='submit'
                  variant='contained'
                  loading={isSubmitting}
                >
                  {isAddMode ? 'Add Region' : 'Edit Region'}
                </LoadingButton>
              </Stack>
            </Card>
          </form>
        )}
      </Grid>
    </Grid>
  );
};

export default RegionForm;
